import { useState, useEffect } from 'react';
import { useDisclosure } from '@nextui-org/react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { ArrowLeft, Check, ChevronRight, CircleX, Minus } from 'lucide-react';

import { GET_ME } from '../graphql/queries';
import { trackEvent, trackPageView } from '../utils/gt';
import { formatDay, proPrice, subscriptionState, trialContext, trialEndFromNow } from '../utils/subscription';
import { ModalProSuccess } from '../components/ModalProSuccess';
import { ProFeatureList } from '../components/subscription/ProFeatureList';
import '../styles/pages/conta.css';
import { PreviewArt } from '../components/app/PreviewArt';

const API_URL = import.meta.env.VITE_GRAPHQL_URL.replace('/graphql', '');

// Free and Pro side by side: true / false is a check or a dash, anything else is the value itself
const COMPARE = [
  {
    key: 'groupWorkouts',
    rows: [
      ['savedWorkouts', 'savedFree', 'savedPro'],
      ['buildWorkouts', true, true],
      ['aiWorkouts', false, true],
      ['aiSplits', false, true],
      ['progress', false, true],
      ['alexa', true, true],
    ],
  },
  {
    key: 'groupContent',
    rows: [
      ['library', true, true],
      ['academy', false, true],
      ['diet', false, true],
    ],
  },
  {
    key: 'groupExport',
    rows: [
      ['pdfWorkouts', false, true],
      ['pdfDiet', false, true],
    ],
  },
];

export const Plans = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const plans = (key, options) => t(`pages.Plans.${key}`, options);
  const ui = (key, options) => t(`pages.Plans.ui.${key}`, options);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  // Read once: the query string is cleared right after so a reload doesn't replay it
  const [hasSuccess] = useState(searchParams.get('success') === 'true');
  const [hasCanceled] = useState(searchParams.get('canceled') === 'true');

  const { isOpen: isSuccessOpen, onOpen: onSuccessOpen, onOpenChange: onSuccessOpenChange, onClose: onSuccessClose } = useDisclosure();

  const { data: dataMe, loading: isLoadingMe, refetch: refetchMe } = useQuery(GET_ME);

  useEffect(() => {
    trackPageView('plans', `${t('appUrl')}/plans`, '/plans');
  }, [t]);

  useEffect(() => {
    if (hasSuccess) {
      refetchMe();
      onSuccessOpen();
    }
    if (hasSuccess || hasCanceled) setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Coming back from Stripe with the back button restores this page from cache: stop the spinner
  useEffect(() => {
    const reset = (event) => event.persisted && setIsCheckoutLoading(false);
    window.addEventListener('pageshow', reset);
    return () => window.removeEventListener('pageshow', reset);
  }, []);

  const me = dataMe?.me;
  const { isTrial, isCanceling, isPastDue, hasSubscription, trialAvailable } = subscriptionState(me);
  const lang = t('lang');
  const price = proPrice(lang);
  const periodEnd = formatDay(me?.subscriptionCurrentPeriodEnd, lang);
  const context = trialContext(me);

  const handleGetPro = async () => {
    trackEvent('plans_upgrade_click', 'Plans - Click Get Pro', 'plans', true);
    setIsCheckoutLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          successUrl: `${window.location.origin}/plans?success=true`,
          cancelUrl: `${window.location.origin}/plans?canceled=true`,
          locale: t('lang'),
        }),
      });
      const { url } = response.ok ? await response.json() : {};
      if (!url) throw new Error('No checkout url');
      window.location.href = url;
    } catch {
      setIsCheckoutLoading(false);
      toast.error(t('notifications.checkoutError'), { toastId: 'checkout-error' });
    }
  };

  // Back to wherever the user came from; straight links land on the dashboard
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/dashboard'));

  const checkoutButton = (
    <button
      type="button"
      className="mt-btn mt-btn--primary mt-offer__cta"
      onClick={handleGetPro}
      disabled={isCheckoutLoading}
      aria-busy={isCheckoutLoading || undefined}
    >
      {isCheckoutLoading && <span className="mt-btn__spinner" aria-hidden="true" />}
      {plans('getPro', { context })}
    </button>
  );

  // Members see where their subscription stands and the way to it, instead of a button to buy what they have
  let memberLine = ui('renews', { date: periodEnd });
  if (isPastDue) memberLine = ui('pastDue');
  else if (isCanceling) memberLine = ui('ends', { date: periodEnd });
  else if (isTrial) memberLine = ui('trialEnds', { date: periodEnd });

  const cell = (value, isPro) => {
    if (value === true)
      return (
        <td className={`mt-compare__mark${isPro ? ' is-pro' : ''}`}>
          <Check aria-hidden="true" />
          <span className="mt-sr-only">{ui('included')}</span>
        </td>
      );
    if (value === false)
      return (
        <td className="mt-compare__mark is-none">
          <Minus aria-hidden="true" />
          <span className="mt-sr-only">{ui('notIncluded')}</span>
        </td>
      );
    return <td className={`mt-compare__value${isPro ? ' is-pro' : ''}`}>{ui(`compare.${value}`)}</td>;
  };

  return (
    <div className="mt-root mt-root--app mt-offer-page">
      <ModalProSuccess
        isOpen={isSuccessOpen}
        onOpenChange={onSuccessOpenChange}
        onClose={onSuccessClose}
        trialEnd={isTrial ? periodEnd : null}
      />

      <header className="mt-editor-bar">
        <button type="button" className="mt-editor-bar__back" onClick={goBack} aria-label={ui('back')}>
          <ArrowLeft aria-hidden="true" />
          <span className="mt-editor-bar__back-label">{ui('back')}</span>
        </button>
        <span />
        <span />
      </header>

      <div className="mt-offer">
        {hasCanceled && (
          <p className="mt-offer__notice" role="status">
            <CircleX aria-hidden="true" />
            <span>
              <b>{ui('canceledTitle')}.</b> {ui('canceledText')}
            </span>
          </p>
        )}

        <div className="mt-offer__hero">
          <PreviewArt kind="pro" size="lg" />
          <h1 className="mt-offer__title">{ui('headline')}</h1>
          <p className="mt-offer__sub">
            {hasSubscription && ui('subMember')}
            {!hasSubscription && trialAvailable && (
              <>
                <b>{ui('trialLead')}</b>
                {ui('trialRest', { price })}
              </>
            )}
            {!hasSubscription && !trialAvailable && ui('sub_noTrial', { price })}
          </p>

          {!isLoadingMe &&
            (hasSubscription ? (
              <div className="mt-glist mt-offer__current">
                <dl>
                  <div className="mt-glist__row">
                    <dt className="mt-glist__k">{ui('currentPlan')}</dt>
                    <dd className="mt-glist__v">
                      <b>{isPastDue ? ui('pastDueName') : ui('proName')}</b>
                    </dd>
                  </div>
                </dl>
                {(periodEnd || isPastDue) && <p className="mt-glist__row">{memberLine}</p>}
                <Link
                  className="mt-glist__row mt-glist__btn"
                  to="/account"
                  onClick={() => trackEvent('plans_see_subscription', 'Plans - See Subscription', 'plans', true)}
                >
                  <span className="mt-glist__k mt-glist__title">{ui('seeSubscription')}</span>
                  <ChevronRight className="mt-glist__end" aria-hidden="true" />
                </Link>
              </div>
            ) : (
              <div className="mt-offer__act">
                {checkoutButton}
                <p className="mt-offer__fine">
                  {trialAvailable
                    ? ui('fineTrial', { date: formatDay(trialEndFromNow(), lang), price })
                    : ui('fineNoTrial')}
                </p>
              </div>
            ))}
        </div>

        <section className="mt-offer__section" aria-labelledby="mt-offer-includes">
          <h2 id="mt-offer-includes" className="mt-offer__heading">
            {ui('includes')}
          </h2>
          <ProFeatureList labelledBy="mt-offer-includes" className="mt-feats--two mt-feats--span-last" />
        </section>

        <section className="mt-offer__section" aria-labelledby="mt-offer-compare">
          <h2 id="mt-offer-compare" className="mt-offer__heading">
            {ui('compareTitle')}
          </h2>
          <div className="mt-glist">
            <table className="mt-compare">
              <thead>
                <tr>
                  <td />
                  <th scope="col">{ui('free')}</th>
                  <th scope="col" className="is-pro">
                    {ui('pro')}
                  </th>
                </tr>
              </thead>
              {COMPARE.map((group) => (
                <tbody key={group.key}>
                  <tr className="mt-compare__group">
                    <th scope="colgroup" colSpan={3}>
                      {ui(`compare.${group.key}`)}
                    </th>
                  </tr>
                  {group.rows.map(([label, free, pro]) => (
                    <tr key={label}>
                      <th scope="row">{ui(`compare.${label}`)}</th>
                      {cell(free, false)}
                      {cell(pro, true)}
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        </section>

        <p className="mt-offer__fine mt-offer__fine--left">{ui('footnote')}</p>

        {!isLoadingMe && !hasSubscription && <div className="mt-offer__end">{checkoutButton}</div>}
      </div>
    </div>
  );
};
